import React, { useCallback, useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import { Plus, Upload, RotateCcw, Search, Loader2, ChevronLeft, ChevronRight } from "lucide-react"
import { useGetPricesGridQuery, useDeletePricesMutation } from "../../app/services/transfer/prices"
import { formatDate } from "./utils/date/common"
import { goTo, withQuery } from "./utils/grid/common"
import PricesTable from "./components/PricesTable"
import { Field, SelectInput, TextInput } from "../transfer_price_detail/components/Formcontrols"
import Button from "../../shared/components/ui/Button"

const DEFAULT_FILTERS = {
  state_id: "",
  terminal_id: "",
  company_id: "",
  transfer_type: "",
  direction: "",
  period: "",
}
const PER_PAGE_OPTIONS = [10, 20, 50, 100]

const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return v
}

const pageList = (cur, total) => {
  const nums = [...new Set([1, total, cur - 1, cur, cur + 1])]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b)
  const out = []
  nums.forEach((p, i) => {
    if (i && p - nums[i - 1] > 1) out.push(`gap-${p}`)
    out.push(p)
  })
  return out
}

const App = ({ urls }) => {
  const { t } = useTranslation()
  const tt = (key, def) => t(key, { defaultValue: def })

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [q, setQ] = useState("")
  const debouncedQ = useDebounced(q)
  const [page, setPage] = useState(1)
  const [per, setPer] = useState(20)
  const [busyIds, setBusyIds] = useState([])

  const args = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({ ...filters, q: debouncedQ.trim(), page, per }).filter(([, v]) => v !== "")
      ),
    [filters, debouncedQ, page, per]
  )

  const { data, isLoading, isFetching, isError, refetch } = useGetPricesGridQuery(args)
  const [deletePrices] = useDeletePricesMutation()

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS)
    setQ("")
    setPage(1)
  }

  // После удаления последней строки на странице > 1 — возвращаемся назад
  useEffect(() => {
    if (data && data.routes.length === 0 && page > 1) setPage((p) => p - 1)
  }, [data, page])

  const buildNewUrl = useCallback((params) => withQuery(urls.newPrice, params), [urls.newPrice])
  const buildEditUrl = useCallback((id) => urls.editPrice.replace("__ID__", id), [urls.editPrice])

  const handleDelete = useCallback(
    async (ids, routeName) => {
      const message = routeName
        ? `${t("admin.delete_route_confirm", "Удалить трассу и все её цены")}: «${routeName}» (${ids.length})?`
        : t("admin.are_you_sure", "Вы уверены?")
      if (!window.confirm(message)) return

      setBusyIds((prev) => [...prev, ...ids])
      try {
        await deletePrices({ ids }).unwrap()
        await refetch()
      } finally {
        setBusyIds((prev) => prev.filter((id) => !ids.includes(id)))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deletePrices, refetch]
  )

  const transferTypeLabels = useMemo(
    () => Object.fromEntries((data?.filters.transfer_types ?? []).map((o) => [o.value, o.label])),
    [data]
  )

  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / per))
  const from = total ? (page - 1) * per + 1 : 0
  const to = Math.min(page * per, total)

  return (
    <div className="bg-bg-secondary min-h-screen p-3 sm:p-4 md:p-6">
      <div className="w-full flex flex-col gap-4 sm:gap-5">
        {/* Шапка */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-app-base-strong">
              {t("admin.transfer_prices_title", "Цены на трансферы")}
            </h1>
            <p className="text-text-muted mt-1">
              {t("admin.transfer_prices_hint", "Управление трассами и ценами по типам транспорта")}
            </p>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {urls.import && (
              <Button
                color="third"
                size="lg"
                onClick={() => goTo(urls.import)}
                className="flex-1 sm:flex-none border border-app-content-faint/20"
              >
                <Upload className="w-4 h-4" />
                {t("admin.import", "Импорт")}
              </Button>
            )}
            <Button
              color="second"
              size="lg"
              className="flex-1 sm:flex-none"
              onClick={() => goTo(buildNewUrl({}))}
            >
              <Plus className="w-4 h-4" />
              {t("admin.add_route", "Добавить трассу")}
            </Button>
          </div>
        </div>

        {/* Фильтры */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface-main rounded-card shadow-sm border border-app-content-faint/10 p-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[repeat(5,1fr)_1.3fr_auto] gap-4 items-end">
            <Field label={t("admin.country")}>
              <SelectInput
                options={data?.filters.states ?? []}
                value={filters.state_id}
                placeholder={t("admin.all", "Все")}
                onChange={(v) => setFilter("state_id", v)}
              />
            </Field>
            <Field label={t("admin.terminal")}>
              <SelectInput
                options={data?.filters.terminals ?? []}
                value={filters.terminal_id}
                placeholder={t("admin.all", "Все")}
                onChange={(v) => setFilter("terminal_id", v)}
              />
            </Field>
            <Field label={t("admin.company")}>
              <SelectInput
                options={data?.filters.companies ?? []}
                value={filters.company_id}
                placeholder={t("admin.all", "Все")}
                onChange={(v) => setFilter("company_id", v)}
              />
            </Field>
            <Field label={t("admin.transfer_type")}>
              <SelectInput
                options={data?.filters.transfer_types ?? []}
                valueKey="value"
                labelKey="label"
                value={filters.transfer_type}
                placeholder={t("admin.all", "Все")}
                onChange={(v) => setFilter("transfer_type", v)}
              />
            </Field>
            <Field label={t("admin.direction", "Направление")}>
              <SelectInput
                options={[
                  { value: "departure", label: t("admin.direction_departure", "Туда") },
                  { value: "return", label: t("admin.direction_return", "Обратно") },
                ]}
                valueKey="value"
                labelKey="label"
                value={filters.direction}
                placeholder={t("admin.all", "Все")}
                onChange={(v) => setFilter("direction", v)}
              />
            </Field>
            <Field label={t("admin.search_by_name", "Поиск по названию")}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
                <TextInput
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value)
                    setPage(1)
                  }}
                  className="!pl-10"
                />
              </div>
            </Field>
            <Button
              color="third"
              size="lg"
              onClick={resetFilters}
              className="h-[44px] !py-0 border border-app-content-faint/20"
            >
              <RotateCcw className="w-4 h-4" />
              {t("admin.reset_filters", "Сбросить фильтры")}
            </Button>
          </div>
        </motion.div>

        {/* Периоды (сезоны) */}
        {data && data.periods.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto sm:flex-wrap pb-1">
            {[
              { key: "", label: t("admin.all_periods", "Все периоды") },
              ...data.periods.map((p) => ({
                key: p.key,
                label: `${formatDate(p.from)} – ${formatDate(p.to)}`,
              })),
            ].map((p) => (
              <button
                key={p.key || "all"}
                type="button"
                onClick={() => setFilter("period", p.key)}
                className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-button text-sm transition-colors ${
                  filters.period === p.key
                    ? "bg-brand-main text-text-inverse"
                    : "bg-surface-main text-text-secondary border border-app-content-faint/20 hover:bg-bg-secondary"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Таблица */}
        {isLoading ? (
          <div className="flex justify-center py-24 text-text-muted">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : isError || !data ? (
          <div className="py-24 text-center text-text-muted">
            {t("admin.load_error", "Не удалось загрузить данные")}
          </div>
        ) : (
          <div className={`transition-opacity ${isFetching ? "opacity-60" : ""}`}>
            <PricesTable
              routes={data.routes}
              transportTypes={data.transport_types}
              buildNewUrl={buildNewUrl}
              buildEditUrl={buildEditUrl}
              onDelete={handleDelete}
              busyIds={busyIds}
              transferTypeLabels={transferTypeLabels}
            />
          </div>
        )}

        {/* Футер */}
        {data && total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-text-muted">
              {t("admin.shown", "Показано")} {from}–{to} {t("admin.of", "из")} {total}{" "}
              {t("admin.routes_word", "трасс")}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-button border border-app-content-faint/20 text-text-secondary disabled:opacity-disabled hover:bg-surface-main transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {pageList(page, totalPages).map((p) =>
                typeof p === "string" ? (
                  <span key={p} className="px-1 text-text-muted">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`min-w-[36px] px-3 py-1.5 rounded-button text-sm transition-colors ${
                      p === page
                        ? "bg-brand-main text-text-inverse"
                        : "border border-app-content-faint/20 text-text-secondary hover:bg-surface-main"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-button border border-app-content-faint/20 text-text-secondary disabled:opacity-disabled hover:bg-surface-main transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm text-text-muted">
              {t("admin.per_page", "Показывать по")}
              <select
                value={per}
                onChange={(e) => {
                  setPer(Number(e.target.value))
                  setPage(1)
                }}
                className="h-9 rounded-input border border-app-content-faint/30 bg-surface-main px-2 text-app-content outline-none focus:border-brand-main"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
    </div>
  )
}

export default App
